import { Injectable } from '@angular/core';
import { splitArray, toKeyValueArray } from '@anecdotes/utils';
import { GridRow, GridView } from '../../models';
import { SearchDefinitionModel } from '@anecdotes/design-system/data-manipulation';
import { Placement } from '@ng-bootstrap/ng-bootstrap';

export const ORIGINAL_ROW_IDENTIFIER = 'originalrowIdentifier';

@Injectable()
export class GridViewBuilderService {
  buildGridView(
    data: { [key: string]: (string | string[] | any[])[], [ORIGINAL_ROW_IDENTIFIER]?: (string | string[] | any[])[] },
    idHeader?: string,
    excludeSearchDefinitionHeaders?: string[],
    headersMetadata?: { [key: string]: { infoTooltipText?: string,  placement?: Placement, widthPx?: number, maxWidthPx?: number, hidden?: boolean } }
  ): GridView {
    const { originalrowIdentifier, ...remainedData } = data ?? {};
    const dict = toKeyValueArray(remainedData); // get keyvalue pairs where key is property name and value is property value
    const filteredHeaderValues = dict.filter(({ key, value }) => key || (!key && (value as []).length));
    const header = filteredHeaderValues.map(({ key }) => key); // take property names as columns. Each property name is column name. Count of properties equal to columns number
    const rows: (string | string[])[] = [];

    if (dict.length && dict[0]?.value?.forEach) {
      dict[0]?.value?.forEach((_, i) => rows.push(...dict.map(({ value }) => this.makeCellFlat(value[i])))); // split rows by columns
    }

    const displayedRows = splitArray(rows, header.length).map((cells, rowIndex) => {
      const result: GridRow = {
        rowId: rowIndex,
        cellsObject: {},
        simplifiedCellsObject: {},
        cells: cells.map((cell) => Array.isArray(cell) ? cell.map((item) => item?.toString()) : cell?.toString()),
        ...(originalrowIdentifier && { [ORIGINAL_ROW_IDENTIFIER]: originalrowIdentifier[rowIndex] }),
      };

      cells.forEach((cell, index) => {
        const simplifiedCell = Array.isArray(cell) ? cell.join(' ') : cell;
        if (idHeader && idHeader === header[index]) {
          result.rowId = simplifiedCell;
        }
        result.cellsObject[header[index]] = cell;
        result.simplifiedCellsObject[header[index]] = simplifiedCell;
      });

      return result;
    });
    const isEmpty = !rows.filter((row) => row.length).length;

    return {
      header,
      headersMetadata,
      rows: isEmpty ? [] : displayedRows,
      rawData: data,
      searchDefinitions: this.buildSearchDefinition(header, excludeSearchDefinitionHeaders),
    };
  }

  private makeCellFlat(cell: string | any[]): any[] {
    if (Array.isArray(cell)) {
      return cell.reduce((prev, current) => {
        return [...prev, ...this.makeCellFlat(current)];
      }, []);
    }

    return [cell];
  }

  private buildSearchDefinition(
    header: string[],
    excludeSearchDefinitionHeaders?: string[]
  ): SearchDefinitionModel<any>[] {
    let filtered = header;
    if (excludeSearchDefinitionHeaders) {
      filtered = header.filter((currHeader) => !excludeSearchDefinitionHeaders.includes(currHeader));
    }

    return filtered.map((headerKey, index) => ({
      propertyId: `row-${index}`,
      propertySelector: (obj: GridRow) => obj.simplifiedCellsObject[headerKey],
    }));
  }
}
